// SinglePeriodInventoryNormal.js
import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, InputNumber, message, Select } from "antd";
import axios from "axios";

const { Option } = Select;

const SinglePeriodInventoryNormal = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [inputData, setInputData] = useState({
    mode: "Revenue and cost per unit",
    // Mode 'Revenue and cost per unit' fields
    revenue: 30,
    cost: 20,
    salvage: 2,
    // Mode 'Marginal loss and Marginal Profit' fields
    marginal_profit: 18,
    marginal_loss: 10,
    // Common fields for normal distribution
    mean: 100,
    std_dev: 15,
  });
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const getAuthToken = () => localStorage.getItem("access_token");

  // Load existing file from DB
  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else {
      // FIX #2: Reset to default values when creating new file
      setInputData({
        mode: "Revenue and cost per unit",
        revenue: 30,
        cost: 20,
        salvage: 2,
        marginal_profit: 18,
        marginal_loss: 10,
        mean: 100,
        std_dev: 15,
        ...initialConfig,
      });
      setResults(null);
      setIsSaved(false);
      setActiveTab("input");
    }
  }, [fileId, initialConfig]);

  const loadData = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      const res = await axios.get(
        `/api/retrieve_single_period_normal/${id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setInputData(res.data.input_data);
      setResults(res.data.output_data);
      setIsSaved(true);
      setActiveTab("output");
    } catch (err) {
      console.error("Load error:", err);
      message.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  // Save / Update API
  const saveData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      // Create payload matching API expectations
      const payload = {
        name: fileName || "Single Period Inventory (Normal)",
        input_data: {
          mode: inputData.mode,
          mean: inputData.mean,
          std_dev: inputData.std_dev,
          ...(inputData.mode === "Revenue and cost per unit" && {
            revenue: inputData.revenue,
            cost: inputData.cost,
            salvage: inputData.salvage,
          }),
          ...(inputData.mode === "Marginal loss and Marginal Profit" && {
            marginal_profit: inputData.marginal_profit,
            marginal_loss: inputData.marginal_loss,
          }),
        },
      };

      console.log("Sending payload:", JSON.stringify(payload, null, 2));

      let res;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (fileId) {
        res = await axios.put(
          `/api/update_single_period_normal/${fileId}/`,
          payload,
          config
        );
      } else {
        res = await axios.post(
          `/api/save_single_period_normal/`,
          payload,
          config
        );
      }

      setResults(res.data.output_data);
      setIsSaved(true);
      setActiveTab("output");
      message.success(fileId ? "Updated successfully" : "Saved successfully");
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      message.error(`Failed to save: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to update input fields
  const handleInputChange = (field, value) => {
    setInputData((prev) => ({ ...prev, [field]: value || 0 }));
  };

  // ================== INPUT TABLE ==================
  const renderInputTable = () => {
    if (!inputData) return null;

    const mode = inputData.mode;

    return (
      <Card title="Input Data">
        <div style={{ marginBottom: 16 }}>
          <label>Mode: </label>
          <Select
            value={mode}
            style={{ width: 280, marginLeft: 8 }}
            onChange={(value) => handleInputChange("mode", value)}
          >
            <Option value="Revenue and cost per unit">Revenue and cost per unit</Option>
            <Option value="Marginal loss and Marginal Profit">Marginal loss and Marginal Profit</Option>
          </Select>
        </div>

        {mode === "Revenue and cost per unit" && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              <b>Revenue per unit:</b>{" "}
              <InputNumber
                value={inputData.revenue}
                onChange={(val) => handleInputChange("revenue", val)}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <b>Cost per unit:</b>{" "}
              <InputNumber
                value={inputData.cost}
                onChange={(val) => handleInputChange("cost", val)}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <b>Salvage value per unit:</b>{" "}
              <InputNumber
                value={inputData.salvage}
                onChange={(val) => handleInputChange("salvage", val)}
              />
            </div>
          </div>
        )}

        {mode === "Marginal loss and Marginal Profit" && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              <b>Marginal Profit:</b>{" "}
              <InputNumber
                value={inputData.marginal_profit}
                onChange={(val) => handleInputChange("marginal_profit", val)}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <b>Marginal Loss:</b>{" "}
              <InputNumber
                value={inputData.marginal_loss}
                onChange={(val) => handleInputChange("marginal_loss", val)}
              />
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <b>Mean Demand (μ):</b>{" "}
            <InputNumber
              value={inputData.mean}
              onChange={(val) => handleInputChange("mean", val)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <b>Standard Deviation (σ):</b>{" "}
            <InputNumber
              min={0}
              value={inputData.std_dev}
              onChange={(val) => handleInputChange("std_dev", val)}
            />
          </div>
        </div>

        <Button
          type="primary"
          onClick={saveData}
          loading={isLoading}
          style={{ marginTop: 16 }}
        >
          {isSaved ? "Update" : "Save"}
        </Button>
      </Card>
    );
  };

  // ================== OUTPUT TABLE ==================
  const renderOutputTable = () => {
    if (!results) return <p>No results yet. Save first.</p>;

    const { output_data, chart_url } = results;

    if (!output_data) return <p>No output data available.</p>;

    return (
      <Card title="Output Results">
        <Card title="Results" style={{ marginBottom: 16 }}>
          {output_data.revenue_per_unit !== undefined && (
            <p>
              <b>Revenue per unit:</b> {output_data.revenue_per_unit}
            </p>
          )}
          {output_data.cost_per_unit !== undefined && (
            <p>
              <b>Cost per unit:</b> {output_data.cost_per_unit}
            </p>
          )}
          {output_data.salvage_value_per_unit !== undefined && (
            <p>
              <b>Salvage value per unit:</b> {output_data.salvage_value_per_unit}
            </p>
          )}
          <p>
            <b>Marginal Profit:</b> {output_data.marginal_profit}
          </p>
          <p>
            <b>Marginal Loss:</b> {output_data.marginal_loss}
          </p>
          {output_data.critical_ratio !== undefined && (
            <p>
              <b>Critical Ratio:</b> {output_data.critical_ratio}{" "}
              {output_data.critical_ratio_note && `(${output_data.critical_ratio_note})`}
            </p>
          )}
          <p>
            <b>Optimal Order Quantity (Q*):</b> {output_data.optimal_order_quantity}
          </p>
        </Card>

        {chart_url && (
          <Card title="Expected Profit Chart">
            <img
              src={chart_url}
              alt="Profit vs Order Quantity"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Card>
        )}
      </Card>
    );
  };

  return (
    <Card title={fileName || "Single Period Inventory (Normal)"}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "input",
            label: "Input",
            children: renderInputTable()
          },
          {
            key: "output",
            label: "Output",
            children: renderOutputTable()
          }
        ]}
      />
    </Card>
  );
};

export default SinglePeriodInventoryNormal;
